import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AppKitService } from '../../services/ appkit.service';

function readCookie(name: string): string | null {
  const source = typeof document !== 'undefined' ? document.cookie : '';
  if (!source) return null;
  const parts = source.split(';');
  for (const part of parts) {
    const [rawKey, ...rest] = part.trim().split('=');
    if (rawKey === name) {
      const value = rest.join('=');
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    }
  }
  return null;
}

export const httpLoggingInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const toastr = inject(ToastrService);
  const appKitService = inject(AppKitService);
  let headers = req.headers;
  const method = req.method.toUpperCase();
  const isMutating =
    method === 'GET' ||
    method === 'POST' ||
    method === 'PUT' ||
    method === 'PATCH' ||
    method === 'DELETE';

  const xsrfCookie = readCookie('admin_csrf_token');

  if (xsrfCookie) {
    const token = xsrfCookie.includes('|')
      ? xsrfCookie.split('|')[0]
      : xsrfCookie;

    headers = headers.set('x-xsrf-token', token);
  } else if (isMutating) {
    console.warn('CSRF-Token not found!');
  }
  req = req.clone({
    headers,
    withCredentials: true
  });

  console.log(`[HTTP] REQUEST ► ${req.method} ${req.url}`);

  return next(req).pipe(
    tap((event: HttpEvent<unknown>) => {
      if (event instanceof HttpResponse) {
        console.log(
          `[HTTP] STATUS ◄ ${event.status} ${event.statusText} | ${req.url}`
        );
      }
    }),
    catchError((error: HttpErrorResponse) => {
      console.error(`[HTTP] ERROR ◄ ${error.status} | ${req.url}`);
      console.error('[HTTP] Error body:', error.error);

      let message = 'An unexpected error occurred';

      switch (error.status) {
        case 400:
          message = error.error?.message || 'Invalid Request';
          toastr.error(message, 'Bad Request (400)');
          break;

        case 401:
          // Skip 401 handling for the logout endpoint itself to prevent loops
          if (!req.url.includes('/admin-auth/logout')) {
            appKitService.handleSessionExpired();
          }
          break;

        case 403:
          message = 'Access forbidden. You do not have permission.';
          toastr.error(message, 'Forbidden (403)');
          break;

        case 404:
          if (req.method.toUpperCase() !== 'GET') {
            message = error.error?.message || 'Resource Not Found';
            toastr.error(message, 'Not Found (404)');
          }
          break;

        case 500:
          message = 'Internal Server Error. Please try again later.';
          toastr.error(message, 'Server Error (500)');
          break;

        case 0:
          message = 'Cannot reach the server. Check your network or backend.';
          toastr.error(message, 'Connection Error');
          break;

        default:
          toastr.error(
            error.error?.message || message,
            `Error (${error.status})`
          );
      }
      return throwError(() => error);
    })
  );
};
